/**
 * Toast notifications.
 *
 * The container is an `aria-live` region so screen-reader users hear
 * confirmations ("Added to your bag") that sighted users see. `polite` means
 * it waits for a natural pause rather than interrupting.
 */

import { el } from '@/lib/dom'

const ICONS = {
  success:
    '<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M16.5 5.5 8 14l-4.5-4.5"/></svg>',
  error:
    '<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M10 6v5M10 14h.01"/><circle cx="10" cy="10" r="8"/></svg>',
  info: '<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M10 9v5M10 6h.01"/><circle cx="10" cy="10" r="8"/></svg>',
} as const

export type ToastType = keyof typeof ICONS

let region: HTMLElement | null = null

function getRegion(): HTMLElement {
  if (region) return region

  region = el('div', {
    class: 'toast-region',
    role: 'status',
    'aria-live': 'polite',
    'aria-atomic': 'false',
  })
  document.body.append(region)

  return region
}

export function toast(message: string, type: ToastType = 'success', duration = 3800): void {
  const node = el('div', { class: `toast toast--${type}` }, [])
  node.innerHTML = `<span class="toast__icon">${ICONS[type]}</span><span></span>`

  // Assign the message as text, never as HTML — it can contain server data.
  const label = node.querySelector('span:last-child')
  if (label) label.textContent = message

  getRegion().append(node)

  const dismiss = () => {
    node.classList.add('is-leaving')
    node.addEventListener('animationend', () => node.remove(), { once: true })
    // Fallback in case the animation is suppressed by reduced-motion.
    setTimeout(() => node.remove(), 400)
  }

  const timer = setTimeout(dismiss, duration)
  node.addEventListener('click', () => {
    clearTimeout(timer)
    dismiss()
  })
}
